import React, { useState } from 'react';
import { Link } from 'react-router-dom';

const API_BASE = 'https://api.zitotrontech.com';

function AgentoPage() {
  const [checkoutEmail, setCheckoutEmail] = useState('');
  const [activePlan, setActivePlan] = useState<string | null>(null);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [checkoutError, setCheckoutError] = useState('');
  const [notifyEmail, setNotifyEmail] = useState('');
  const [notifyPlatform, setNotifyPlatform] = useState('');
  const [notifySuccess, setNotifySuccess] = useState('');

  const handleCheckout = async (plan: string) => {
    if (!activePlan) {
      setActivePlan(plan);
      return;
    }

    if (!checkoutEmail || !checkoutEmail.includes('@')) {
      setCheckoutError('Please enter a valid email address.');
      return;
    }

    setCheckoutLoading(true);
    setCheckoutError('');

    try {
      const res = await fetch(`${API_BASE}/v1/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: checkoutEmail, plan }),
      });

      if (!res.ok) {
        throw new Error('Checkout failed. Please try again.');
      }

      const data = await res.json();
      if (data.url) {
        window.location.href = data.url;
      }
    } catch (err: any) {
      setCheckoutError(err.message || 'Something went wrong.');
    } finally {
      setCheckoutLoading(false);
    }
  };

  const handleNotify = async (platform: string) => {
    if (!notifyPlatform) {
      setNotifyPlatform(platform);
      return;
    }

    if (!notifyEmail || !notifyEmail.includes('@')) {
      return;
    }

    try {
      await fetch(`${API_BASE}/v1/notify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: notifyEmail, platform }),
      });
      setNotifySuccess(`We'll notify you when ${platform} is available.`);
      setNotifyEmail('');
      setNotifyPlatform('');
    } catch {
      // silent fail for notify
    }
  };

  return (
    <div className="agento-page">
      {/* Navigation */}
      <nav className="agento-nav">
        <div className="agento-nav-inner">
          <Link to="/" className="agento-nav-brand">ZitotronTech</Link>
          <div className="agento-nav-links">
            <a href="#features">Features</a>
            <a href="#pricing">Pricing</a>
            <a href="#download">Download</a>
            <a href="#faq">FAQ</a>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="agento-hero">
        <div className="agento-hero-inner">
          <h1 className="agento-hero-title">Your AI agents. One dashboard.</h1>
          <p className="agento-hero-subhead">
            Multi-window terminal manager for AI coding agents. Works with Claude, Cursor, Aider, and any CLI tool.
          </p>
          <p className="agento-hero-subtitle">Made by developers, for developers. Junior to senior.</p>
          <div className="agento-hero-buttons">
            <a href="#download" className="agento-btn-primary">Download Free Trial</a>
            <a href="#pricing" className="agento-btn-outline">View Pricing</a>
          </div>
        </div>
      </section>

      {/* Agent Showcase */}
      <section className="agento-agents">
        <div className="container">
          <h2>Works with your favorite AI coding agents</h2>
          <div className="agento-agent-list">
            <span className="agento-agent-tag">Claude Code</span>
            <span className="agento-agent-tag">Cursor</span>
            <span className="agento-agent-tag">Aider</span>
            <span className="agento-agent-tag">Cody</span>
            <span className="agento-agent-tag">Continue</span>
            <span className="agento-agent-tag">Windsurf</span>
            <span className="agento-agent-tag agento-agent-tag-any">any CLI agent</span>
          </div>
        </div>
      </section>

      {/* Feature Grid */}
      <section id="features" className="agento-features">
        <div className="container">
          <h2>Everything you need to manage AI agents</h2>
          <div className="agento-feature-grid">
            <div className="agento-feature-card">
              <div className="agento-feature-icon">[ ][ ]</div>
              <h3>Multi-Display</h3>
              <p>Manage sessions across multiple monitors. Each display gets its own set of terminal windows.</p>
            </div>
            <div className="agento-feature-card">
              <div className="agento-feature-icon">&gt;_</div>
              <h3>Session Resume</h3>
              <p>Pick up where you left off, even after restart. Your layout and sessions persist automatically.</p>
            </div>
            <div className="agento-feature-card">
              <div className="agento-feature-icon">[*]</div>
              <h3>Smart Status</h3>
              <p>Real-time status detection -- working, waiting, idle, error. Know what every agent is doing at a glance.</p>
            </div>
            <div className="agento-feature-card">
              <div className="agento-feature-icon">&gt;&gt;</div>
              <h3>Quick Response</h3>
              <p>Respond to any terminal from any window. Jump between agents without switching displays.</p>
            </div>
            <div className="agento-feature-card">
              <div className="agento-feature-icon">((o))</div>
              <h3>Voice Input</h3>
              <p>Hands-free control with voice commands. Dictate responses while reviewing code elsewhere.</p>
            </div>
            <div className="agento-feature-card">
              <div className="agento-feature-icon">{'{...}'}</div>
              <h3>Workspace Manager</h3>
              <p>Save and restore your multi-window layouts. Switch between project configurations instantly.</p>
            </div>
          </div>
        </div>
      </section>

      {/* How it Works */}
      <section className="agento-how">
        <div className="container">
          <h2>Get started in seconds</h2>
          <div className="agento-steps">
            <div className="agento-step">
              <div className="agento-step-number">1</div>
              <h3>Download</h3>
            </div>
            <div className="agento-step-arrow">&rarr;</div>
            <div className="agento-step">
              <div className="agento-step-number">2</div>
              <h3>Install</h3>
            </div>
            <div className="agento-step-arrow">&rarr;</div>
            <div className="agento-step">
              <div className="agento-step-number">3</div>
              <h3>Launch</h3>
            </div>
            <div className="agento-step-arrow">&rarr;</div>
            <div className="agento-step">
              <div className="agento-step-number">4</div>
              <h3>Done</h3>
            </div>
          </div>
          <p className="agento-how-note">No config required. Start using immediately.</p>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="agento-pricing">
        <div className="container">
          <h2>Simple, transparent pricing</h2>
          <div className="agento-pricing-grid">
            <div className="agento-pricing-card">
              <h3>Monthly</h3>
              <div className="agento-price">
                <span className="agento-price-amount">$20</span>
                <span className="agento-price-period">/month</span>
              </div>
              <p className="agento-price-tagline">Flexible, cancel anytime</p>
              <ul className="agento-price-features">
                <li>All features</li>
                <li>All platforms</li>
                <li>Auto-updates</li>
                <li>3-day free trial</li>
              </ul>
              {activePlan === 'monthly' ? (
                <div className="agento-checkout-inline">
                  <input
                    type="email"
                    placeholder="your@email.com"
                    value={checkoutEmail}
                    onChange={(e) => setCheckoutEmail(e.target.value)}
                    className="agento-email-input"
                  />
                  <button
                    className="agento-btn-primary"
                    onClick={() => handleCheckout('monthly')}
                    disabled={checkoutLoading}
                  >
                    {checkoutLoading ? 'Processing...' : 'Continue'}
                  </button>
                </div>
              ) : (
                <button className="agento-btn-primary" onClick={() => handleCheckout('monthly')}>
                  Subscribe
                </button>
              )}
            </div>

            <div className="agento-pricing-card agento-pricing-highlight">
              <div className="agento-best-value">Best Value</div>
              <h3>Yearly</h3>
              <div className="agento-price">
                <span className="agento-price-amount">$199</span>
                <span className="agento-price-period">/year</span>
              </div>
              <p className="agento-price-tagline">Save $41</p>
              <ul className="agento-price-features">
                <li>All features</li>
                <li>All platforms</li>
                <li>Auto-updates</li>
                <li>3-day free trial</li>
              </ul>
              {activePlan === 'yearly' ? (
                <div className="agento-checkout-inline">
                  <input
                    type="email"
                    placeholder="your@email.com"
                    value={checkoutEmail}
                    onChange={(e) => setCheckoutEmail(e.target.value)}
                    className="agento-email-input"
                  />
                  <button
                    className="agento-btn-primary"
                    onClick={() => handleCheckout('yearly')}
                    disabled={checkoutLoading}
                  >
                    {checkoutLoading ? 'Processing...' : 'Continue'}
                  </button>
                </div>
              ) : (
                <button className="agento-btn-primary" onClick={() => handleCheckout('yearly')}>
                  Subscribe
                </button>
              )}
            </div>
          </div>
          {checkoutError && <p className="agento-checkout-error">{checkoutError}</p>}
        </div>
      </section>

      {/* Download */}
      <section id="download" className="agento-download">
        <div className="container">
          <h2>Download Agento</h2>
          <div className="agento-download-grid">
            <div className="agento-download-card">
              <h3>Linux</h3>
              <p>AppImage -- works on any distribution</p>
              <a href="#" className="agento-btn-primary">Download for Linux</a>
            </div>
            <div className="agento-download-card agento-download-soon">
              <h3>Windows</h3>
              <p>.exe -- coming soon</p>
              {notifyPlatform === 'windows' ? (
                <div className="agento-notify-inline">
                  <input
                    type="email"
                    placeholder="your@email.com"
                    value={notifyEmail}
                    onChange={(e) => setNotifyEmail(e.target.value)}
                    className="agento-email-input"
                  />
                  <button className="agento-btn-outline" onClick={() => handleNotify('windows')}>
                    Notify Me
                  </button>
                </div>
              ) : (
                <button className="agento-btn-outline" onClick={() => handleNotify('windows')}>
                  Coming soon -- notify me
                </button>
              )}
            </div>
            <div className="agento-download-card agento-download-soon">
              <h3>Mac</h3>
              <p>.dmg -- coming soon</p>
              {notifyPlatform === 'mac' ? (
                <div className="agento-notify-inline">
                  <input
                    type="email"
                    placeholder="your@email.com"
                    value={notifyEmail}
                    onChange={(e) => setNotifyEmail(e.target.value)}
                    className="agento-email-input"
                  />
                  <button className="agento-btn-outline" onClick={() => handleNotify('mac')}>
                    Notify Me
                  </button>
                </div>
              ) : (
                <button className="agento-btn-outline" onClick={() => handleNotify('mac')}>
                  Coming soon -- notify me
                </button>
              )}
            </div>
          </div>
          {notifySuccess && <p className="agento-notify-success">{notifySuccess}</p>}
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="agento-faq">
        <div className="container">
          <h2>Frequently Asked Questions</h2>
          <div className="agento-faq-list">
            <div className="agento-faq-item">
              <h3>What agents does it support?</h3>
              <p>Any CLI-based AI coding agent. If it runs in a terminal, Agento can manage it. Claude Code, Cursor, Aider, Cody, Continue, Windsurf, and more.</p>
            </div>
            <div className="agento-faq-item">
              <h3>Do I need to configure anything?</h3>
              <p>No. Just install and launch. Agento detects your displays and agents automatically. Zero configuration required.</p>
            </div>
            <div className="agento-faq-item">
              <h3>What platforms are supported?</h3>
              <p>Linux is available now. Windows and Mac support are coming soon.</p>
            </div>
            <div className="agento-faq-item">
              <h3>Is there a trial?</h3>
              <p>Yes. 3 days, full features, no credit card required.</p>
            </div>
            <div className="agento-faq-item">
              <h3>Can I use it offline?</h3>
              <p>Yes. After one-time activation, Agento works fully offline.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="agento-footer">
        <div className="container">
          <div className="agento-footer-content">
            <p>&copy; 2026 Zito Tron Tech LLC. All rights reserved.</p>
            <div className="agento-footer-links">
              <Link to="/terms">Terms</Link>
              <Link to="/privacy">Privacy</Link>
              <Link to="/agento/eula">EULA</Link>
              <a href="mailto:support@zitotrontech.com">support@zitotrontech.com</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default AgentoPage;
